import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Backup-Health-Indicator (Phase 5Z.19q)
 * Tiny status pill in the admin header showing how recent the last full backup is.
 *  green  ≤ 7 days     ShieldCheck / emerald
 *  yellow ≤ 14 days    ShieldAlert / amber
 *  red    > 14 days    ShieldAlert / rose
 *  none   no backup    ShieldQuestion / zinc
 *
 * Click → navigates to /einstellungen?tab=disaster-recovery so the admin can
 * inspect/create backups without hunting through tabs.
 *
 * Polls once on mount + every 5 minutes while the page is alive.
 */
const COLORS = {
  green: { dot: "bg-emerald-500", text: "text-emerald-400", Icon: ShieldCheck, label: "Backup OK" },
  yellow: { dot: "bg-amber-500", text: "text-amber-400", Icon: ShieldAlert, label: "Backup älter" },
  red: { dot: "bg-rose-500", text: "text-rose-400", Icon: ShieldAlert, label: "Backup veraltet" },
  none: { dot: "bg-zinc-500", text: "text-zinc-400", Icon: ShieldQuestion, label: "Kein Backup" },
};

function formatAge(h) {
  if (h == null) return "—";
  if (h < 1) return "<1 h";
  if (h < 24) return `${Math.round(h)} h`;
  const d = h / 24;
  if (d < 14) return `${Math.round(d)} Tg`;
  return `${Math.round(d)} Tg`;
}

export default function BackupHealthIndicator() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const fetch = () => {
      api.get("/admin/full-backup/health")
        .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
        .catch(() => { if (alive) { setData(null); setLoading(false); } });
    };
    fetch();
    const t = setInterval(fetch, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading || !data) return null;

  const status = data.count === 0 ? "none" : (data.status || "none");
  const conf = COLORS[status] || COLORS.none;
  const { Icon } = conf;

  const ageLabel = data.count === 0
    ? "Noch kein Voll-Backup"
    : `Letztes Voll-Backup vor ${formatAge(data.age_hours)}`;

  const tooltip = [
    ageLabel,
    data.last_backup_at ? `Erstellt: ${new Date(data.last_backup_at).toLocaleString("de-AT")}` : null,
    `${data.count} Backup${data.count === 1 ? "" : "s"} gespeichert`,
    `Wöchentliches Auto-Backup: ${data.weekly_enabled ? "AN" : "AUS"}`,
    "Klicken für Verwaltung →",
  ].filter(Boolean).join("\n");

  return (
    <button
      type="button"
      onClick={() => navigate("/einstellungen?tab=disaster-recovery")}
      title={tooltip}
      data-testid="backup-health-indicator"
      data-status={status}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs hover:bg-zinc-800/70 transition-colors ${conf.text}`}
    >
      <span className={`relative flex items-center justify-center w-2 h-2`}>
        {status === "red" && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${conf.dot} opacity-70 animate-ping`} />
        )}
        <span className={`relative inline-flex w-2 h-2 rounded-full ${conf.dot}`} />
      </span>
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden md:inline font-medium">
        {data.count === 0 ? "Kein Backup" : `Backup · ${formatAge(data.age_hours)}`}
      </span>
    </button>
  );
}
